import React, { useState, useEffect } from 'react';
import Loader from '../components/SkeletonLoader';
import moment from 'moment';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { pathOr, propOr, isEmpty } from 'ramda';
import {
  Button,
  Card,
  Link,
  Banner,
  Icon,
  Tooltip,
  Frame,
  Stack,
  Heading,
  ButtonGroup,
  Toast,
  TextStyle,
} from '@shopify/polaris';
import {
  EditMinor,
  ViewMinor,
  AddMajorMonotone,
  IllustrationMajorMonotone,
  NavigationMajorMonotone,
  DnsSettingsMajorMonotone,
  MobileCancelMajorMonotone,
  DuplicateMinor,
} from '@shopify/polaris-icons';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { GET_CANDIDATES, GET_JOBS, GET_CURRENT_SUBSCRIPTION, GET_SETTINGS_INFO, GET_JOBS_VISITS } from '../apollo/queries';
import { UPDATE_ONBOARDING_PROFILE, UPDATE_PROFILE, CREATE_SHOP_SUBSCRIPTION } from '../apollo/mutation';

import Onboarding from '../components/OnboardingWizard';
import emptyState from '../../assets/images/empty_state.png';

const WelcomeBanner = ({ onDismiss }) => (
  <>
    <div className={'onboarding__banner'}>
      <Banner
        title={
          <Stack>
            <Stack.Item fill>
              <Heading>
                Welcome to ShopHire! Schedule a 1 on 1 onboarding call to walk through the app and get some recruiting
                recommendations.
              </Heading>
            </Stack.Item>
          </Stack>
        }
        onDismiss={onDismiss}
        status='info'
      />
    </div>
    <br />
  </>
);

const LedgerCheckBanner = ({ onDismiss }) => (
  <>
    <div className={'onboarding__banner'}>
      <Banner title='Getting overcharged on 3PL invoices?' onDismiss={onDismiss} status='info'>
        <p>
          Shipping and fulfillment invoices are notoriously hard to audit. If you use a third-party logistics
          provider, LedgerCheck scans your invoices for overcharges and billing errors — free to check.
        </p>
        <Button url='https://ledger-check.netlify.app' external>
          Check my invoices
        </Button>
      </Banner>
    </div>
    <br />
  </>
);

const Dashboard = ({ history, shopifyToken, shopifyDomain, shopOrigin, apiKey, app, careerPageLink }) => {
  const { push: changeRoute } = history;
  const { data: dataProfile, refetch: refetchProfile } = useQuery(GET_SETTINGS_INFO, {
    variables: { shopifyToken },
  });
  const { loading, data } = useQuery(GET_JOBS, { variables: { shopifyToken, templates: false } });
  const { data: candidatesData, loading: loadingCandidates } = useQuery(GET_CANDIDATES, { variables: { shopifyToken } });
  const { data: subscriptionData, refetch } = useQuery(GET_CURRENT_SUBSCRIPTION, { variables: { shopifyToken } });
  const { data: jobsData = {}, loading: loadingVisits } = useQuery(GET_JOBS_VISITS, {
    variables: { shopifyToken, templates: false },
  });
  const [toastStatus, setToastStatus] = useState({ status: false, message: '' });
  const { jobs: jobsWrapper = {} } = { ...jobsData };
  const { jobs: jobVisits = [] } = { ...jobsWrapper };

  const visits = jobVisits
    .map(({ visits = [] }) => {
      return visits.map((v) => ({ ...v }));
    })
    .reduce((a, b) => [...a, ...b], [])
    .filter(({ startedAt }) => {
      return moment(startedAt).isBetween(moment().subtract(7, 'days'), moment());
    });

  const [updateOnboardingProfile] = useMutation(UPDATE_ONBOARDING_PROFILE);
  const [updateProfile] = useMutation(UPDATE_PROFILE);
  const jobs = pathOr([], ['jobs', 'jobs'])(data);
  const candidates = propOr([], 'candidates')(candidatesData);
  const shop = propOr({}, 'shop')(subscriptionData);
  const careerPortalSet = propOr(false, 'careerPortalSet')(shop);
  const shopProfile = propOr({}, 'profile')(shop);
  const isCareerPortalSet = !!careerPortalSet;
  const onboardingControls = propOr({}, 'onboardingControls')(shopProfile);

  const weekCandidates = (candidates || []).filter((c) => {
    const { createdAt } = { ...c };
    const lastWeek = moment().subtract(7, 'days');
    return moment(createdAt).isBetween(lastWeek, moment());
  });

  const [createShopSubscription] = useMutation(CREATE_SHOP_SUBSCRIPTION);

  const {
    welcome_banner_dismiss = false,
    onboarding_dismiss = false,
    hiring_team_card_dismiss = false,
    hiring_locations_card_dismiss = false,
    webhooks_card_dismiss = false,
    google_jobs_card_dismiss = false,
    zapier_card_dismiss = false,
    careers_page_link_card_dismiss = false,
    monday_card_dismiss = false,
    ledgercheck_prompt_dismiss = false,
  } = { ...onboardingControls };

  const createdDays = moment(new Date()).diff(moment(shop?.createdAt), 'days');
  const hasOnboardingInactivity = createdDays > 30;

  const showEmptyState = isEmpty(jobs) && isEmpty(candidates);

  if (loading || loadingCandidates) {
    return <Loader metrics={true} />;
  }

  const dismissOnboarding = () => {
    updateOnboardingControl({ onboarding_dismiss: true });
  };

  const dismissWelcomeBanner = () => {
    updateOnboardingControl({ welcome_banner_dismiss: true });
  };

  const dismissLedgerCheckPrompt = () => {
    updateOnboardingControl({ ledgercheck_prompt_dismiss: true });
  };

  const dismissHiringTeamCard = () => {
    updateOnboardingControl({ hiring_team_card_dismiss: true });
  };

  const dismissHiringLocationsCard = () => {
    updateOnboardingControl({ hiring_locations_card_dismiss: true });
  };

  const dismissWebhooksCard = () => {
    updateOnboardingControl({ webhooks_card_dismiss: true });
  };

  const dismissGoogleJobsCard = () => {
    updateOnboardingControl({ google_jobs_card_dismiss: true });
  };

  const dismissZapierCard = () => {
    updateOnboardingControl({ zapier_card_dismiss: true });
  };

  const dismissCareersPageLinkCard = () => {
    updateOnboardingControl({ careers_page_link_card_dismiss: true });
  };

  const dismissMondayCard = () => {
    updateOnboardingControl({ monday_card_dismiss: true });
  };

  const updateOnboardingControl = async (onboardingInput, reload = true) => {
    try {
      await updateOnboardingProfile({
        variables: { shopifyToken, onboardingInput },
      });
      reload && !!refetch && refetch();
    } catch (err) {
      console.log(err);
    }
  };

  const onboardingTabs = [
    { label: 'Post job', icon: AddMajorMonotone, checked: !isEmpty(jobs) },
    { label: 'Customize career portal', icon: IllustrationMajorMonotone, checked: isCareerPortalSet },
    {
      label: 'Add careers link',
      icon: NavigationMajorMonotone,
      checked: propOr(false, 'careers_link_added')(onboardingControls),
    },
    {
      label: 'Review company profile',
      icon: DnsSettingsMajorMonotone,
      checked: propOr(false, 'company_profile_reviewed')(onboardingControls),
    },
  ];

  const createSubscription = (subscriptionType, callback) => {
    const response = createShopSubscription({
      variables: {
        shopifyToken,
        subscriptionType,
      },
    });

    const errors = pathOr([], ['data', 'createShopSubscription', 'errors'])(response);
    const emptyErrors = isEmpty(errors);
    if (emptyErrors) {
      callback();
    }
  };

  const showOnboarding = !onboarding_dismiss && moment().diff(moment(shop.createdAt), 'days') <= 15;

  const welcomeModalMarkup = () => {
    return <></>;
    /*return (
      <WelcomeModal
        open={showWelcomeModal}
        onCloseModal={() => setShowWelcomeModal(false)}
        shopifyToken={shopifyToken}
        onUpdateProfile={updateProfile}
        onCreateSubscription={createSubscription}
        shop={(dataProfile || {}).shop}
        onUpdateOnboardingControl={updateOnboardingControl}
      />
    );*/
  };

  if (showOnboarding) {
    return (
      <Frame>
        <Onboarding
          app={app}
          data={dataProfile}
          refetch={refetchProfile}
          shopifyToken={shopifyToken}
          onUpdateProfile={updateProfile}
          onDismiss={dismissOnboarding}
          shopOrigin={shopOrigin}
          apiKey={apiKey}
        />
      </Frame>
    );
  }

  if (showEmptyState) {
    /*!onboarding_dismiss && !isCompletedOnboarding ? (
      <Frame>
        {welcomeModalMarkup()}
        <div className={'dashboard'}>
          {!welcome_banner_dismiss && <WelcomeBanner onDismiss={dismissWelcomeBanner} />}
          <Onboarding
            changeRoute={changeRoute}
            shopifyToken={shopifyToken}
            shopifyDomain={shopifyDomain}
            onDismiss={dismissOnboarding}
            refetch={refetch}
            tabs={onboardingTabs}
          />
        </div>
      </Frame>
    ) : (*/
    return (
      <div className='dashboard__empty-state'>
        {welcomeModalMarkup()}
        <div className='dashboard__empty-state-info'>
          <h1 className='dashboard__empty-state-title'>Post jobs and start recruiting top talent </h1>
          <p className='dashboard__empty-state-description'>
            Create the perfect job description and broadcast it on Google for Jobs
          </p>
          <Button onClick={() => changeRoute('/set-job')} size='large' primary>
            Post job
          </Button>
        </div>
        <img src={emptyState} alt='Dashboard empty state' className='dashboard__empty-state-image' />
      </div>
    );
    //);
  }

  const openJobs = jobs.filter(({ status }) => status === 'open');
  const openJobsLength = openJobs.length;
  const subscriptionStatus = pathOr('', ['shop', 'currentSubscription', 'status'])(subscriptionData);
  const subscriptionType = pathOr('', ['shop', 'currentSubscription', 'subscriptionType'])(subscriptionData);

  const isSubscriptionActive = subscriptionStatus === 'active';
  const isStarterSubscription = subscriptionType === 'Starter';
  const isGrowthSubscription = subscriptionType === 'Growth';
  const isPremiumSubscription = subscriptionType === 'Premium';

  const jobsLength = jobs.length;

  const currentSubscription = propOr({}, 'currentSubscription')(shop);

  const activeCandidates = jobs
    .map(({ totalCandidates = {} }) => {
      const { active = 0 } = { ...totalCandidates };
      return active;
    })
    .reduce((a, b) => a + b, 0);
  const subscriptionPlan = currentSubscription.subscriptionPlan || {
    number_jobs: isGrowthSubscription ? 5 : 3,
  };
  const disablePostJobButton = openJobsLength >= subscriptionPlan.number_jobs;

  return (
    <Frame>
      <div className={'dashboard'}>
        {welcomeModalMarkup()}
        <br />
        {!welcome_banner_dismiss && (
          <>
            <WelcomeBanner onDismiss={dismissWelcomeBanner} />
          </>
        )}
        {!ledgercheck_prompt_dismiss && <LedgerCheckBanner onDismiss={dismissLedgerCheckPrompt} />}
        {
          <>
            {/*!onboarding_dismiss && !isCompletedOnboarding && (
              <Onboarding
                changeRoute={changeRoute}
                shopifyToken={shopifyToken}
                shopifyDomain={shopifyDomain}
                onDismiss={dismissOnboarding}
                refetch={refetch}
                tabs={onboardingTabs}
              />
            )*/}
            {/*isCareerPortalSet && showLinksBanner && (
              <>
                <Banner status='info' onDismiss={dismissCareerPortalLinkBanner}>
                  Add a Careers or Jobs link to your sites navigation.
                  <ol>
                    <li>
                      Copy <strong>{`${shopifyDomain}/a/careers`}</strong>
                    </li>
                    <li>
                      Go to{' '}
                      <Link url={`https://${shopifyDomain}/admin/menus`} external={true} removeUnderline>
                        <strong>Online Store &gt; Navigation</strong>
                      </Link>
                    </li>
                  </ol>
                </Banner>
                <br />
              </>
            )*/}

            {!hiring_team_card_dismiss && !hasOnboardingInactivity && false && (
              <Card>
                <Card.Header
                  title={'Get your team involved with the hiring process'}
                  actions={[
                    {
                      icon: MobileCancelMajorMonotone,
                      onAction: () => dismissHiringTeamCard(),
                    },
                  ]}
                />
                <Card.Section>
                  <Stack spacing='loose' vertical>
                    <p>Keep team members informed with notifications about jobs and candidates.</p>
                    <ButtonGroup>
                      <Button
                        onClick={() => {
                          changeRoute(`/settings/hiring-team`);
                          dismissHiringTeamCard();
                        }}>
                        Add hiring team members
                      </Button>
                      <Button plain url={'https://help.shophire.co/article/27-hiring-team'} external={true}>
                        Learn more
                      </Button>
                    </ButtonGroup>
                  </Stack>
                </Card.Section>
              </Card>
            )}

            {!hiring_locations_card_dismiss && !hasOnboardingInactivity && false && (
              <>
                <br />
                <Card>
                  <Card.Header
                    title={'Speed up the job posting process with hiring locations'}
                    actions={[
                      {
                        icon: MobileCancelMajorMonotone,
                        onAction: () => dismissHiringLocationsCard(),
                      },
                    ]}
                  />
                  <Card.Section>
                    <Stack spacing='loose' vertical>
                      <p>With hiring locations, you have the option to quickly add location information to job postings.</p>
                      <ButtonGroup>
                        <Button
                          onClick={() => {
                            dismissHiringLocationsCard();
                            changeRoute(`/settings/hiring-locations`);
                          }}>
                          Add hiring locations
                        </Button>
                        <Button plain url={'https://help.shophire.co/article/9-hiring-locations'} external={true}>
                          Learn more
                        </Button>
                      </ButtonGroup>
                    </Stack>
                  </Card.Section>
                </Card>
              </>
            )}

            {!google_jobs_card_dismiss && !hasOnboardingInactivity && (
              <Card>
                <Card.Header
                  title={'Submit your career portal sitemap to display your openings on Google for Jobs'}
                  actions={[
                    {
                      icon: MobileCancelMajorMonotone,
                      onAction: () => dismissGoogleJobsCard(),
                    },
                  ]}
                />
                <Card.Section>
                  <Stack spacing='loose' vertical>
                    <p>
                      70% of job searches begin on Google. Adding your career portal sitemap to Google Search Console helps Google
                      find and index your jobs in the Google for Jobs Rich Snippet.
                    </p>
                    <ButtonGroup>
                      <Button
                        onClick={() => {
                          dismissGoogleJobsCard();
                          changeRoute(`/google-settings`);
                        }}>
                        Connect to Google for Jobs
                      </Button>
                      <Button
                        plain
                        url={'https://help.shophire.co/article/43-how-to-set-up-google-for-jobs-indexing-api'}
                        external={true}>
                        Learn more
                      </Button>
                    </ButtonGroup>
                  </Stack>
                </Card.Section>
              </Card>
            )}
            {!careers_page_link_card_dismiss && !hasOnboardingInactivity && (
              <>
                <br />
                <Card>
                  <Card.Header
                    title={'Make your careers page easy to find with a nav link'}
                    actions={[
                      {
                        icon: MobileCancelMajorMonotone,
                        onAction: () => dismissCareersPageLinkCard(),
                      },
                    ]}
                  />
                  <Card.Section>
                    <Stack spacing='loose' vertical>
                      <p>
                        {`Your career portal page is: `}
                        <TextStyle variation='subdued'>{careerPageLink}</TextStyle>
                      </p>
                      <ButtonGroup>
                        <CopyToClipboard
                          text={careerPageLink}
                          onCopy={() => {
                            setToastStatus({
                              status: true,
                              message: 'Copied to clipboard',
                            });
                            dismissCareersPageLinkCard();
                          }}>
                          <Button icon={DuplicateMinor}>{'Copy nav link'}</Button>
                        </CopyToClipboard>
                        <Button plain url={'https://help.shophire.co/article/15-linking-to-your-careers-page'} external={true}>
                          Learn more
                        </Button>
                      </ButtonGroup>
                    </Stack>
                  </Card.Section>
                </Card>
              </>
            )}
            {!monday_card_dismiss && !hasOnboardingInactivity && (isGrowthSubscription || isPremiumSubscription) && (
              <>
                <br />
                <Card>
                  <Card.Header
                    title={'Integrate ShopHire with Monday.com Work OS'}
                    actions={[
                      {
                        icon: MobileCancelMajorMonotone,
                        onAction: () => dismissMondayCard(),
                      },
                    ]}
                  />
                  <Card.Section>
                    <Stack spacing='loose' vertical>
                      <p>
                        <b>Monday.com</b> is a Work OS that powers teams to run projects and processes with confidence. It's a
                        flexible platform that enables teams to manage work, meet deadlines, and build a culture of accountability
                        and inclusion. Use ShopHire's integration to synch job and candidate data with your recruiting team's
                        workspace.
                      </p>
                      <ButtonGroup>
                        <a
                          href='https://auth.monday.com/oauth2/authorize?client_id=25db2c23ff612ac154ed2764b9c026b3&response_type=install'
                          target='_blank'
                          onClick={() => dismissMondayCard()}>
                          {' '}
                          <img
                            alt='Add to monday.com'
                            height='42'
                            src='https://dapulse-res.cloudinary.com/image/upload/f_auto,q_auto/remote_mondaycom_static/uploads/Tal/4b5d9548-0598-436e-a5b6-9bc5f29ee1d9_Group12441.png'
                          />{' '}
                        </a>
                        <Button plain url={'https://help.shophire.co/article/47-monday-com'} external={true}>
                          Learn more
                        </Button>
                      </ButtonGroup>
                    </Stack>
                  </Card.Section>
                </Card>
              </>
            )}
            {!zapier_card_dismiss && !hasOnboardingInactivity && (isGrowthSubscription || isPremiumSubscription) && (
              <>
                <br />
                <Card>
                  <Card.Header
                    title={'Integrate ShopHire with 2,000+ apps using Zapier'}
                    actions={[
                      {
                        icon: MobileCancelMajorMonotone,
                        onAction: () => dismissZapierCard(),
                      },
                    ]}
                  />
                  <Card.Section>
                    <Stack spacing='loose' vertical>
                      <p>
                        Automate your hiring process with triggers and actions with Zapier. Autopost new jobs on Facebook and
                        Linkedin, send candidate information to Slack, Asana or ClickUp to manage interviews, onboarding and more.
                      </p>
                      <ButtonGroup>
                        <Button
                          url={'https://zapier.com/apps/shophire-ca119476/integrations'}
                          external
                          onClick={() => dismissZapierCard()}>
                          Get started with Zapier
                        </Button>
                        <Button plain url={'https://help.shophire.co/article/44-zapier'} external={true}>
                          Learn more
                        </Button>
                      </ButtonGroup>
                    </Stack>
                  </Card.Section>
                </Card>
              </>
            )}
            <br />
            <div className={'analytics__resume'}>
              <Card>
                <Card.Section>
                  <div className='dashboard__resume-card'>
                    <span className='dashboard__resume-title'>{openJobsLength}</span>
                    <span>
                      <Link onClick={() => changeRoute(`/job-list`) } removeUnderline>
                        Open jobs
                      </Link>
                    </span>
                  </div>
                </Card.Section>
              </Card>
              <Card>
                <Card.Section>
                  <div className='dashboard__resume-card'>
                    <span className='dashboard__resume-title'>{weekCandidates.length}</span>
                    <span>New candidates this week</span>
                  </div>
                </Card.Section>
              </Card>
              <Card>
                <Card.Section>
                  <div className='dashboard__resume-card'>
                    <span className='dashboard__resume-title'>{activeCandidates}</span>
                    <span>
                      <Link onClick={() => changeRoute(`/candidate-list/active`)} removeUnderline>
                        Active candidates
                      </Link>
                    </span>
                  </div>
                </Card.Section>
              </Card>
              <Card>
                <Card.Section>
                  <div className='dashboard__resume-card'>
                    <span className='dashboard__resume-title'>{(visits || []).length}</span>
                    <span>
                      <Link onClick={() => changeRoute(`/analytics/job-description-views`) } removeUnderline>
                        Job page views this week
                      </Link>
                    </span>
                  </div>
                </Card.Section>
              </Card>
            </div>
            <br />
            {!!candidates.length && (
              <Card title='5 most recent candidates'>
                {candidates.slice(0, 5).map(({ id, jobTitle, createdAt, name, lastName }) => (
                  <Card.Section key={id}>
                    <div className='dashboard__row'>
                      <Link onClick={() => changeRoute(`/candidate/${id}`) } removeUnderline>
                        {' '}
                        {name} {lastName}{' '}
                      </Link>
                      <span className={'dashboard__row-text dashboard__row-dark'}>{jobTitle}</span>
                      <span className={'dashboard__row-dark'} style={{ textAlign: 'center' }}>
                        {createdAt}
                      </span>
                    </div>
                  </Card.Section>
                ))}
                <Card.Section>
                  <Button onClick={() => changeRoute('/candidate-list')}>View all candidates</Button>
                </Card.Section>
              </Card>
            )}
            <Card title={openJobsLength ? `${openJobsLength} open jobs` : 'Post a job'}>
              {openJobs.length ? (
                <>
                  {disablePostJobButton && (
                    <Card.Section>
                      <Banner status='warning'>
                        You've reached your account plan's open job limit. To open more jobs{' '}
                        <Link onClick={() => changeRoute('/account-plan')} removeUnderline>
                          <strong>upgrade your account plan</strong>
                        </Link>
                      </Banner>
                    </Card.Section>
                  )}
                  {openJobs.map(({ id, title, totalCandidates }) => (
                    <Card.Section key={`section_${id}`}>
                      <div className='dashboard__job__row'>
                        <span className='dashboard__job__row-title'>
                          <span style={{ cursor: 'pointer' }} onClick={() => changeRoute(`/job-candidates/${id}`)}>
                            {title}
                          </span>{' '}
                          <span className={'dashboard__job__row-buttons'}>
                            <div
                              className={'job_button_item'}
                              onClick={() => window.open(`https://${shopifyDomain}/a/careers/jobs/${id}`)}>
                              <Tooltip active content='View on career portal' active={false} dismissOnMouseOut={true}>
                                <Icon source={ViewMinor} />
                              </Tooltip>
                            </div>
                            <div className={'job_button_item'} onClick={() => changeRoute(`/set-job/${id}`)}>
                              <Tooltip active content='Edit job' active={false} dismissOnMouseOut={true}>
                                <Icon source={EditMinor} />
                              </Tooltip>
                            </div>
                          </span>
                        </span>
                        {!!totalCandidates && !!totalCandidates.active ? (
                          <Link onClick={() => changeRoute(`/job-candidates/${id}/active`)} removeUnderline>
                            {totalCandidates.active} active candidates
                          </Link>
                        ) : (
                          <p>0 active candidates</p>
                        )}
                      </div>
                    </Card.Section>
                  ))}
                </>
              ) : (
                <Card.Section>
                  <p>Create the perfect job posting using our job templates and rich text editor.</p>
                </Card.Section>
              )}
              <Card.Section>
                <Button disabled={disablePostJobButton} onClick={() => changeRoute('/set-job')}>
                  Post job
                </Button>
              </Card.Section>
            </Card>
            {isPremiumSubscription && !webhooks_card_dismiss && !hasOnboardingInactivity && (
              <>
                <br />
                <Card>
                  <Card.Header
                    title={'Integrate ShopHire with other applications using webhooks'}
                    actions={[
                      {
                        icon: MobileCancelMajorMonotone,
                        onAction: () => dismissWebhooksCard(),
                      },
                    ]}
                  />
                  <Card.Section>
                    <Stack spacing='loose' vertical>
                      <p>
                        Connect ShopHire to the tool you already know and love using Zapier or Integromat. Send candidate
                        information to Asana, Airtable or Tello to manage the hiring process.
                      </p>
                      <ButtonGroup>
                        <Button
                          onClick={() => {
                            dismissWebhooksCard();
                            changeRoute(`/settings/notifications`);
                          }}>
                          Create webhooks
                        </Button>
                        <Button plain url={'https://help.shophire.co/article/25-how-create-a-webhook'} external={true}>
                          Learn more
                        </Button>
                      </ButtonGroup>
                    </Stack>
                  </Card.Section>
                </Card>
              </>
            )}
          </>
        }
      </div>
      {toastStatus.status && (
        <Toast content={toastStatus.message} onDismiss={() => setToastStatus({ status: false, message: '' })} />
      )}
    </Frame>
  );
};

export default Dashboard;
